import dayjs from 'dayjs';
import CommonJs from '@/utils/commonJs';
import { formatDateOnlyLocalYMD, toLocalCalendarMidnight } from '@/utils/dateOnlyForApi';
import { DuAnCongViecChiTieuItem, DuAnCongViecTree } from '@/types/action-plan/DuAnView';
import { DuAnCongViecTienDoDTO } from '@/types/action-plan/ActionPlan/DuAnCongViecTienDoDTO';
import { tabletansuat, defaultTabletansuat } from '@/types/performance/bangtuansuat';

export const round2 = (n: number) => Math.round(n * 100) / 100;

/** 0: Tổng; 1: Trung bình; 2: Tiến độ */
export function distributeChiTieuByHinhThuc(total: number, n: number, hinhThuc: number): number[] {
  if (n <= 0) return [];
  const t = Number(total) || 0;
  if (hinhThuc === 2) {
    // Tiến độ: tăng dần, kỳ cuối = chỉ tiêu max (vd. 100 kỳ → 1, 2, …, 100)
    if (n === 1) return [round2(t)];
    const amounts = Array.from({ length: n - 1 }, (_, i) => round2((t * (i + 1)) / n));
    amounts.push(round2(t));
    return amounts;
  }
  if (hinhThuc === 1) {
    return Array(n).fill(round2(t));
  }
  const base = round2(t / n);
  const arr = Array(n).fill(base);
  const sum = arr.reduce((a, b) => a + b, 0);
  const diff = round2(t - sum);
  arr[n - 1] = round2(arr[n - 1] + diff);
  return arr;
}

export function mergeTanSuatFromApi(raw: tabletansuat[]): tabletansuat[] {
  return raw.map((ts) => ({ ...defaultTabletansuat, ...ts }));
}

const TAN_SUAT_MAP: Record<string, number> = {
  day: 1,
  week: 2,
  month: 3,
  report: 4,
  Ngày: 1,
  Tuần: 2,
  Tháng: 3,
  'Báo cáo cuối kỳ': 4,
};

export function resolveTanSuatNumber(recordRow: DuAnCongViecTree): number {
  const tanSuatValue =
    recordRow?.congViec?.tanSuat ||
    recordRow?.tenTanSuat;
  return typeof tanSuatValue === 'number'
    ? tanSuatValue
    : (tanSuatValue ? TAN_SUAT_MAP[tanSuatValue] : undefined) || 3;
}

/**
 * Ngày gán cho DuAnCongViecTienDo.Ngay — khớp CreateTienDo (BE):
 * - Mỗi kỳ: Ngay = listTanSuat[i].ketthuc (ngày cuối kỳ báo cáo).
 * - Cuối kỳ: Ngay = NgayKetThuc công việc (một dòng; ketthuc trong bảng thường = ngày kết thúc CV).
 * Chuẩn hóa đầu ngày local để tránh lệch giờ khi serialize.
 */
export function resolveNgayBaoCaoTienDo(
  ts: tabletansuat,
  prev: DuAnCongViecTienDoDTO | undefined,
  congViecNgayBatDau?: Date | string | null,
  congViecNgayKetThuc?: Date | string | null
): Date {
  const candidates: Array<string | Date | undefined | null> = [
    ts.ketthuc,
    ts.ngay,
    ts.batdau,
    prev?.ngay,
    congViecNgayKetThuc,
    congViecNgayBatDau,
  ];
  for (const raw of candidates) {
    if (raw == null || raw === '') continue;
    const cal = toLocalCalendarMidnight(raw as Date | string);
    if (cal) return cal;
  }
  return dayjs().startOf('day').toDate();
}

/** Khóa snapshot / so sánh thay đổi — cùng trục ngày với resolveNgayBaoCaoTienDo (ưu tiên ketthuc). */
export function chiTieuPeriodDayKey(ts: tabletansuat): string {
  const d = ts.ketthuc ?? ts.ngay ?? ts.batdau;
  if (!d) return String(ts.idbaocao || ts.id || '');
  return formatDateOnlyLocalYMD(d as Date | string) ?? String(ts.idbaocao || ts.id || '');
}

export function buildInitialChiTieuByNgayMap(list: tabletansuat[]): Record<string, number> {
  return (list || []).reduce((acc, ts) => {
    acc[chiTieuPeriodDayKey(ts)] = Number(ts.chiTieu ?? 0);
    return acc;
  }, {} as Record<string, number>);
}

/** Gộp listChiTieu sau Chia chỉ tiêu (cùng khóa ngày với bảng công việc). */
export function mergeListChiTieuItems(
  prevList: DuAnCongViecChiTieuItem[] | null | undefined,
  incoming: DuAnCongViecChiTieuItem[] | null | undefined
): DuAnCongViecChiTieuItem[] {
  if (!incoming?.length) return Array.isArray(prevList) ? [...prevList] : [];
  const prev = Array.isArray(prevList) ? prevList : [];
  const byKey = new Map<string, DuAnCongViecChiTieuItem>();
  const keyOf = (x: DuAnCongViecChiTieuItem): string => {
    const d = x?.ngay;
    if (d == null || d === '') return '';
    return formatDateOnlyLocalYMD(d as Date | string) ?? '';
  };
  prev.forEach((x) => {
    const k = keyOf(x);
    if (k) byKey.set(k, x);
  });
  incoming.forEach((x) => {
    const k = keyOf(x);
    if (!k) return;
    const old = byKey.get(k);
    byKey.set(k, old ? { ...old, ...x } : x);
  });
  return Array.from(byKey.values());
}

export function generateReportsFallback(
  startDate: Date | string,
  endDate: Date | string,
  tanSuat: number,
  totalChiTieu: number
): Array<{
  kyBaoCao: string;
  ngayBatDau: Date;
  ngayKetThuc: Date;
  chiTieu: number;
}> {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const reports: Array<{
    kyBaoCao: string;
    ngayBatDau: Date;
    ngayKetThuc: Date;
    chiTieu: number;
  }> = [];

  if (tanSuat === 4) {
    reports.push({
      kyBaoCao: dayjs(endDate).format('DD/MM/YYYY'),
      ngayBatDau: start.toDate(),
      ngayKetThuc: end.toDate(),
      chiTieu: totalChiTieu,
    });
    return reports;
  }

  let current = start;
  let periodIndex = 1;

  if (tanSuat === 1) {
    while (current.isBefore(end) || current.isSame(end, 'day')) {
      reports.push({
        kyBaoCao: `Ngày ${periodIndex}`,
        ngayBatDau: current.toDate(),
        ngayKetThuc: current.toDate(),
        chiTieu: 0,
      });
      current = current.add(1, 'day');
      periodIndex++;
    }
  } else if (tanSuat === 2) {
    while (current.isBefore(end) || current.isSame(end, 'day')) {
      let periodStart = current.startOf('week').add(1, 'day');
      if (periodStart.isBefore(start)) periodStart = start;
      let periodEnd = current.endOf('week').add(1, 'day');
      if (periodEnd.isAfter(end)) periodEnd = end;
      if (periodStart.isBefore(end) || periodStart.isSame(end, 'day')) {
        reports.push({
          kyBaoCao: `Tuần ${periodIndex}`,
          ngayBatDau: periodStart.toDate(),
          ngayKetThuc: periodEnd.toDate(),
          chiTieu: 0,
        });
        periodIndex++;
      }
      current = periodEnd.add(1, 'day');
      if (current.isAfter(end)) break;
    }
  } else if (tanSuat === 3) {
    while (current.isBefore(end) || current.isSame(end, 'day')) {
      let periodStart = current.startOf('month');
      if (periodStart.isBefore(start)) periodStart = start;
      let periodEnd = current.endOf('month');
      if (periodEnd.isAfter(end)) periodEnd = end;
      if (periodStart.isBefore(end) || periodStart.isSame(end, 'day')) {
        reports.push({
          kyBaoCao: `Tháng ${periodIndex}`,
          ngayBatDau: periodStart.toDate(),
          ngayKetThuc: periodEnd.toDate(),
          chiTieu: 0,
        });
        periodIndex++;
      }
      current = current.add(1, 'month').startOf('month');
      if (current.isAfter(end)) break;
    }
  }

  if (reports.length > 0) {
    const per = totalChiTieu / reports.length;
    reports.forEach((r) => {
      r.chiTieu = round2(per);
    });
    const totalDistributed = reports.reduce((sum, r) => sum + r.chiTieu, 0);
    const difference = totalChiTieu - totalDistributed;
    if (difference !== 0 && reports.length > 0) {
      reports[reports.length - 1].chiTieu += difference;
    }
  }

  return reports;
}

export function reportsToTanSuatList(
  reports: Array<{
    idbaocao?: string;
    id?: string;
    kyBaoCao?: string;
    ngayBatDau: Date | string;
    ngayKetThuc: Date | string;
    chiTieu?: number;
  }>
): tabletansuat[] {
  return reports.map((row) => {
    const idbaocao = row.idbaocao || CommonJs.createTempID();
    const batdau =
      toLocalCalendarMidnight(row.ngayBatDau as Date | string) ??
      (row.ngayBatDau instanceof Date ? row.ngayBatDau : new Date(row.ngayBatDau as string));
    const ketthuc =
      toLocalCalendarMidnight(row.ngayKetThuc as Date | string) ??
      (row.ngayKetThuc instanceof Date ? row.ngayKetThuc : new Date(row.ngayKetThuc as string));
    return {
      ...defaultTabletansuat,
      id: row.id || '',
      idbaocao,
      title: row.kyBaoCao || '',
      batdau,
      ketthuc,
      tungay: typeof row.kyBaoCao === 'string' ? row.kyBaoCao : dayjs(ketthuc).format('DD/MM/YYYY'),
      /** Khớp CreateTienDo: Ngay tiến độ = ngày cuối kỳ (ketthuc) */
      ngay: ketthuc,
      ngay_Temp: ketthuc,
      chiTieu: row.chiTieu ?? 0,
      chiTieuTienDo: row.chiTieu ?? 0,
    };
  });
}

export function indexExistingTienDosById(rows: DuAnCongViecTienDoDTO[]): Map<string, DuAnCongViecTienDoDTO> {
  const map = new Map<string, DuAnCongViecTienDoDTO>();
  for (const row of rows) {
    if (row?.id) map.set(String(row.id), row);
  }
  return map;
}



export function buildTienDosPayload(
  tanSuatList: tabletansuat[],
  ctx: {
    idCongViec: string;
    site: string;
    existingById: Map<string, DuAnCongViecTienDoDTO>;
    cvNgayBatDau?: Date | string | null;
    cvNgayKetThuc?: Date | string | null;
  }
): DuAnCongViecTienDoDTO[] {
  const { idCongViec, site, existingById, cvNgayBatDau, cvNgayKetThuc } = ctx;
  return tanSuatList.map((ts) => {
    const id = String(ts.idbaocao || ts.id || CommonJs.createTempID());
    const prev = existingById.get(id);
    const ngayStr = formatDateOnlyLocalYMD(
      resolveNgayBaoCaoTienDo(ts, prev, cvNgayBatDau, cvNgayKetThuc)
    ) as string;

    // Giữ nguyên dòng từ gettablechiachitieu / dicTienDos; chỉ cập nhật chỉ tiêu UI + ngay chuỗi cho JSON
    return {
      ...prev,
      id,
      site: prev?.site ?? site,
      congViecID: idCongViec,
      chiTieu: ts.chiTieu ?? null,
      hoanThanh: ts.thucTe ?? prev?.hoanThanh ?? null,
      trangThai: ts.trangThai ?? prev?.trangThai ?? null,
      filesDinhKem: ts.filedinhkem ?? prev?.filesDinhKem ?? '',
      ngay: ngayStr,
    } as DuAnCongViecTienDoDTO;
  });
}

/** null = hợp lệ; string = thông báo lỗi */
export function getChiTieuConstraintError(hinhThuc: number, chiTieu: number, values: number[]): string | null {
  if (hinhThuc === 0) {
    const sum = values.reduce((a, b) => a + b, 0);
    const diff = round2(sum - Number(chiTieu || 0));
    if (Math.abs(diff) > 0.01) {
      return `Tổng chỉ tiêu các kỳ (${CommonJs.formatNumber(sum)}) phải bằng chỉ tiêu gốc (${CommonJs.formatNumber(chiTieu)}).`;
    }
  }
  if (hinhThuc === 1) {
    const target = Number(chiTieu || 0);
    const invalidIndex = values.findIndex((v) => round2(v) !== round2(target));
    if (invalidIndex !== -1) {
      return `Hình thức Trung bình: mọi kỳ báo cáo phải = ${CommonJs.formatNumber(target)}.`;
    }
  }
  if (hinhThuc === 2) {
    if (values.length > 0) {
      const target = round2(Number(chiTieu || 0));
      if (round2(values[values.length - 1]) !== target) {
        return `Hình thức Tiến độ: kỳ báo cáo cuối phải bằng chỉ tiêu gốc (${CommonJs.formatNumber(target)}).`;
      }
    }
    for (let i = 1; i < values.length; i++) {
      if (values[i] < values[i - 1]) {
        return 'Hình thức Tiến độ: chỉ tiêu phải tăng dần từ kỳ đầu đến kỳ cuối.';
      }
    }
  }
  return null;
}

export function parseMoneyInput(displayValue: string | undefined): number {
  const cleaned = (displayValue ?? '').replace(/,/g, '').trim();
  if (!cleaned) return 0;
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
}

export function formatMoneyInputDisplay(
  value: number | string | null | undefined,
  userTyping: boolean,
  input: string
): string {
  if (userTyping) return input;
  if (value === null || value === undefined || value === '') return '';
  const n = typeof value === 'number' ? value : Number(value);
  return CommonJs.formatMoney(Number.isFinite(n) ? n : 0);
}
